import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Button from 'tio-reimagined/components/button';
import Modal from 'tio-reimagined/components/modal';
import { VStack } from 'tio-ui/components/layout';
import { updateReportRequest } from '../requests/report';
import { SkipCache } from '@warp-drive/core-types/request';
import type StoreService from 'ember-data/store';
import type Report from '../models/reports';
import type Owner from '@ember/owner';

interface PublishReportDialogSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    onPublish: (report: Report) => void;
    report: Report;
    currentCompanyId?: string | null;
    companyName?: string;
  };
}

export default class PublishReportDialog extends Component<PublishReportDialogSignature> {
  @service declare store: StoreService;

  @tracked isPublishing = false;

  constructor(owner: Owner, args: PublishReportDialogSignature['Args']) {
    super(owner, args);
  }

  get companyId() {
    return this.args.currentCompanyId || this.args.report?.metadata?.publishedCompanyId || null;
  }

  get visibilityLevel() {
    return this.companyId ? 'partner' : 'global';
  }

  get partnerAccessConfig() {
    return this.companyId ? [Number(this.companyId)] : [];
  }

  handlePublish = async () => {
    const report = this.args.report;
    if (!report?.id) return;

    this.isPublishing = true;
    try {
      const metadata = {
        ...(report.metadata || {}),
        scope: 'partner_admin' as const,
        publishedAt: new Date().toISOString(),
        publishedCompanyId: this.companyId || undefined,
      };

      const request = updateReportRequest(report.id, {
        visibilityLevel: this.visibilityLevel,
        partnerAccessConfig: this.partnerAccessConfig,
        metadata,
      });
      request.cacheOptions = { [SkipCache]: true };
      const response = await this.store.request(request);
      const attrs = (response.content as { data: { attributes: Record<string, unknown> } })?.data
        ?.attributes;
      if (attrs) {
        Object.assign(report, attrs);
      }
      this.args.onPublish(report);
      this.args.onClose();
    } catch (error) {
      console.error('Failed to publish report:', error);
    } finally {
      this.isPublishing = false;
    }
  };

  handleCancel = () => {
    this.args.onClose();
  };

  <template>
    <Modal @isOpen={{@isOpen}} @onClose={{this.handleCancel}} @size="md" data-test-publish-dialog>
      <:header>Publish Report</:header>

      <:body>
        <VStack class="flex flex-col gap-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Publishing makes this report visible to partner admins.
          </p>

          <div class="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
            <p class="font-medium text-gray-700 dark:text-gray-300">What will happen</p>
            <ul class="mt-1 list-inside list-disc text-gray-600 dark:text-gray-400">
              <li>
                <span class="font-medium">Scope:</span>
                Partner Admin — this report will be visible outside of admin
              </li>
              <li>
                <span class="font-medium">Visibility:</span>
                {{#if this.companyId}}
                  Partner Shared — only
                  <strong class="text-gray-800 dark:text-gray-200">{{if @companyName @companyName "the selected company"}}</strong>
                  can see this report
                {{else}}
                  Global — all partner admins can see this report
                {{/if}}
              </li>
            </ul>
          </div>
        </VStack>
      </:body>

      <:footer>
        <div class="flex justify-end gap-x-3">
          <Button @appearance="outlined" {{on "click" this.handleCancel}} data-test-publish-cancel>
            Cancel
          </Button>
          <Button
            @intent="primary"
            @isRunning={{this.isPublishing}}
            {{on "click" this.handlePublish}}
            data-test-publish-submit
          >
            {{if this.isPublishing "Publishing..." "Publish"}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
